import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useIsSuperUser } from '@/hooks/useIsSuperUser'
import type { ClientMessage } from '../types'

const labels = {
  pending: 'Pending',
  composition_failed: 'Composition failed',
  delivery_failed: 'Delivery failed',
  pending_delivery: 'Sent',
}

export const messageBodyText = (message: Pick<ClientMessage, 'body'>) =>
  message.body || 'No message was composed.'

export function MessageStatusLabel({ message }: { message: ClientMessage }) {
  const isSuperUser = useIsSuperUser()
  const label =
    message.direction === 'inbound'
      ? 'Received'
      : message.status
        ? (labels[message.status] ?? 'Status unknown')
        : 'Status unknown'
  const showDeliveryHint =
    isSuperUser === true &&
    message.direction === 'outbound' &&
    message.status === 'pending_delivery'
  const statusLabel = (
    <span
      tabIndex={showDeliveryHint ? 0 : undefined}
      className={
        message.status?.endsWith('_failed')
          ? 'text-destructive text-xs'
          : 'text-muted-foreground text-xs'
      }>
      {label}
    </span>
  )
  if (!showDeliveryHint) return statusLabel
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{statusLabel}</TooltipTrigger>
        <TooltipContent>
          Submitted to the messaging provider. Delivery has not been confirmed.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function MessageFailureDetails({ message }: { message: ClientMessage }) {
  const isSuperUser = useIsSuperUser()
  if (
    isSuperUser !== true ||
    !message.failure_reason ||
    !['composition_failed', 'delivery_failed'].includes(message.status ?? '')
  )
    return null
  return (
    <Collapsible className="text-muted-foreground mt-1 text-xs">
      <CollapsibleTrigger asChild>
        <Button variant="link" className="h-auto p-0 text-xs">
          Failure details
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="mt-1 whitespace-pre-wrap break-words">
          {message.failure_reason}
        </p>
      </CollapsibleContent>
    </Collapsible>
  )
}
